import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate } from 'react-router-dom';
import { LanguageProvider } from './i18n/LanguageContext';
import ScrollToTop from './components/ScrollToTop';
import DemoHeartbeat from './components/DemoHeartbeat';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import LegalPage from './pages/LegalPage';
import HomePage from './pages/HomePage';
import PlatformPage from './pages/PlatformPage';
import IndustriesPage from './pages/IndustriesPage';
import TechnologyPage from './pages/TechnologyPage';
import ImplementationPage from './pages/ImplementationPage';
import OperationalImpactPage from './pages/OperationalImpactPage';
import WhyTapelligencePage from './pages/WhyTapelligencePage';
import OperationalExperiencePage from './pages/OperationalExperiencePage';
import OperationalRiskPage from './pages/OperationalRiskPage';
import EnterprisePage from './pages/EnterprisePage';
import ContactPage from './pages/ContactPage';
import OperationalRiskDataPage from './pages/OperationalRiskDataPage';
import WarehousePage from './pages/WarehousePage';
import AirportsPage from './pages/AirportsPage';
import CasinoPage from './pages/CasinoPage';
import HotelsPage from './pages/HotelsPage';
import HealthcarePage from './pages/HealthcarePage';
import StadiumsPage from './pages/StadiumsPage';
import RealEstatePage from './pages/RealEstatePage';
import RestaurantsQSRPage from './pages/RestaurantsQSRPage';
import SideTab from './components/SideTab';
import CompactSecurityFooter from './components/CompactSecurityFooter';
import SecurityPage from './pages/SecurityPage';
import ExposureCalculatorPage from './pages/ExposureCalculatorPage';
import AdminLoginPage from './pages/AdminLoginPage';
import ReviewRequestsPage from './pages/ReviewRequestsPage';
import ExperienceManagerPage from './pages/ExperienceManagerPage';
import ProfileManagerPage from './pages/ProfileManagerPage';
import AccessManagerPage from './pages/AccessManagerPage';
import CapabilitiesManagerPage from './pages/CapabilitiesManagerPage';
import ShowroomPage from './pages/ShowroomPage';
import ShowroomInvitationPage from './pages/ShowroomInvitationPage';
import TheShowPage from './pages/TheShowPage';
import TheShowEntryPage from './pages/TheShowEntryPage';
import ShowroomGalleryPage from './pages/ShowroomGalleryPage';
import ShowroomLoginPage from './pages/ShowroomLoginPage';
import ShowroomGuestsPage from './pages/ShowroomGuestsPage';
import ShowroomsPage from './pages/ShowroomsPage';
import AdminShowroomPreview from './pages/AdminShowroomPreview';
import AdminUsersPage from './pages/AdminUsersPage';
import LinkExpiryDashboardPage from './pages/LinkExpiryDashboardPage';
import React, { useState, useEffect } from 'react';
import './App.css';

const SUPPORTED_LANGUAGES = ['en', 'fr', 'es'];
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Auth context for admin role info
const AdminAuthContext = React.createContext({ role: null, access_type: null, display_name: null });
const useAdminAuth = () => React.useContext(AdminAuthContext);

// Cache auth across mounts so ProtectedRoute never unmounts children during a re-check
let _cachedAuth = null;

const ProtectedRoute = ({ children }) => {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(!_cachedAuth);
  const [authInfo, setAuthInfo] = useState(_cachedAuth);

  useEffect(() => {
    fetch(`${API_URL}/api/auth/check`, { credentials: 'include' })
      .then(r => {
        if (r.ok) return r.json();
        throw new Error('Not authed');
      })
      .then(data => {
        const info = { role: data.role, access_type: data.access_type, display_name: data.display_name };
        _cachedAuth = info;
        setAuthInfo(info);
      })
      .catch(() => {
        _cachedAuth = null;
        navigate('/admin/login', { replace: true });
      })
      .finally(() => setChecking(false));
  }, [navigate]);

  if (checking) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><div className="w-6 h-6 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" /></div>;
  if (!authInfo) return null;
  return <AdminAuthContext.Provider value={authInfo}>{children}</AdminAuthContext.Provider>;
};

const Layout = ({ children }) => {
  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <SideTab />
      <main className="flex-grow">
        {children}
        <CompactSecurityFooter />
      </main>
      <Footer />
    </div>
  );
};

const LanguageRoutes = () => {
  const { lang } = useParams();
  
  if (!SUPPORTED_LANGUAGES.includes(lang)) {
    return <Navigate to="/en" replace />;
  }

  return (
    <LanguageProvider>
      <Layout>
        <Routes>
          <Route index element={<HomePage />} />
          <Route path="platform" element={<PlatformPage />} />
          <Route path="industries" element={<IndustriesPage />} />
          <Route path="technology" element={<TechnologyPage />} />
          <Route path="implementation" element={<ImplementationPage />} />
          <Route path="operational-impact" element={<OperationalImpactPage />} />
          <Route path="why-tapelligence" element={<WhyTapelligencePage />} />
          <Route path="operational-experience" element={<OperationalExperiencePage />} />
          <Route path="operational-risk" element={<OperationalRiskPage />} />
          <Route path="enterprise" element={<EnterprisePage />} />
          <Route path="operational-risk-data" element={<OperationalRiskDataPage />} />
          <Route path="warehouse" element={<WarehousePage />} />
          <Route path="airports" element={<AirportsPage />} />
          <Route path="casino" element={<CasinoPage />} />
          <Route path="hotels" element={<HotelsPage />} />
          <Route path="healthcare" element={<HealthcarePage />} />
          <Route path="stadiums" element={<StadiumsPage />} />
          <Route path="real-estate" element={<RealEstatePage />} />
          <Route path="restaurants-qsr" element={<RestaurantsQSRPage />} />
          <Route path="exposure" element={<ExposureCalculatorPage />} />
          <Route path="security" element={<SecurityPage />} />
          <Route path="contact" element={<ContactPage />} />
          <Route path="terms" element={<LegalPage doc="terms" />} />
          <Route path="privacy" element={<LegalPage doc="privacy" />} />
          <Route path="*" element={<Navigate to={`/${lang}`} replace />} />
        </Routes>
      </Layout>
    </LanguageProvider>
  );
};

const AdminRoute = ({ children }) => (
  <LanguageProvider>
    <ProtectedRoute>{children}</ProtectedRoute>
  </LanguageProvider>
);

// Showroom route: accepts admin OR showroom guest auth
let _cachedShowroomAuth = null;

const ShowroomRoute = ({ children }) => {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(!_cachedShowroomAuth);
  const [authInfo, setAuthInfo] = useState(_cachedShowroomAuth);

  useEffect(() => {
    // Try admin auth first
    fetch(`${API_URL}/api/auth/check`, { credentials: 'include' })
      .then(r => { if (r.ok) return r.json(); throw new Error('no admin'); })
      .then(data => {
        const info = { role: data.role, access_type: data.access_type, display_name: data.display_name, authType: 'admin' };
        _cachedShowroomAuth = info;
        setAuthInfo(info);
        setChecking(false);
      })
      .catch(() => {
        // Try showroom gallery (accepts guest cookie)
        fetch(`${API_URL}/api/showroom/gallery`, { credentials: 'include' })
          .then(r => { if (r.ok) return r.json(); throw new Error('no guest'); })
          .then(() => {
            const info = { role: 'showroom_guest', authType: 'guest' };
            _cachedShowroomAuth = info;
            setAuthInfo(info);
            setChecking(false);
          })
          .catch(() => {
            _cachedShowroomAuth = null;
            navigate('/showroom/login', { replace: true });
            setChecking(false);
          });
      });
  }, [navigate]);

  if (checking) return <div className="min-h-screen bg-slate-950 flex items-center justify-center"><div className="w-6 h-6 border-2 border-slate-600 border-t-slate-300 rounded-full animate-spin" /></div>;
  if (!authInfo) return null;
  return <AdminAuthContext.Provider value={authInfo}>{children}</AdminAuthContext.Provider>;
};

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <DemoHeartbeat />
      <Routes>
        <Route path="/" element={<Navigate to="/en" replace />} />
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route path="/admin/review-requests" element={<AdminRoute><ReviewRequestsPage /></AdminRoute>} />
        <Route path="/admin/experiences" element={<AdminRoute><ExperienceManagerPage /></AdminRoute>} />
        <Route path="/admin/profiles" element={<AdminRoute><ProfileManagerPage /></AdminRoute>} />
        <Route path="/admin/access" element={<AdminRoute><AccessManagerPage /></AdminRoute>} />
        <Route path="/admin/showrooms" element={<AdminRoute><ShowroomsPage /></AdminRoute>} />
        <Route path="/admin/showroom-preview/:showroomId" element={<AdminRoute><AdminShowroomPreview /></AdminRoute>} />
        <Route path="/admin/capabilities" element={<AdminRoute><CapabilitiesManagerPage /></AdminRoute>} />
        <Route path="/admin/users" element={<AdminRoute><AdminUsersPage /></AdminRoute>} />
        <Route path="/admin/showroom-guests" element={<AdminRoute><ShowroomGuestsPage /></AdminRoute>} />
        <Route path="/admin/link-expiry" element={<AdminRoute><LinkExpiryDashboardPage /></AdminRoute>} />
        <Route path="/admin/showroom" element={<ShowroomRoute><ShowroomGalleryPage /></ShowroomRoute>} />
        <Route path="/the-show" element={<TheShowEntryPage />} />
        <Route path="/the-show/experiences" element={<ShowroomRoute><TheShowPage /></ShowroomRoute>} />
        <Route path="/the-show/:experienceId" element={<ShowroomRoute><ShowroomPage /></ShowroomRoute>} />
        <Route path="/showroom/login" element={<ShowroomLoginPage />} />
        <Route path="/experience/admin-preview/:experienceId" element={<ShowroomPage />} />
        <Route path="/experience/:token" element={<ShowroomPage />} />
        <Route path="/invitation/:token" element={<ShowroomInvitationPage />} />
        <Route path="/:lang/*" element={<LanguageRoutes />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
export { useAdminAuth };
